import * as React from 'react';

import { useQuery } from '@apollo/client';

import user from 'js/lib/user';

import queryCourseBranchModuleProgressByCourse from 'bundles/course-v2/queries/GetCourseBranchModuleProgressByCourseQuery.graphql';
import type {
  GetCourseBranchModuleProgressByCourseQuery,
  GetCourseBranchModuleProgressByCourseQueryVariables,
} from 'bundles/course-v2/queries/__generated__/GetCourseBranchModuleProgressByCourseQuery';

type Props = React.PropsWithChildren<{
  courseId: string;
  skip?: boolean;
}>;

export type CourseProgressModuleProgress = {
  moduleId: string;
  moduleIndex: number | null | undefined;
  moduleCompletionStatus: string;
};

type ContextData = {
  /**
   * Combined user and course id
   */
  id?: string;

  /**
   * Array of module progress data for the course
   */
  moduleProgressData?: CourseProgressModuleProgress[];

  /**
   * Loading state for the query
   */
  loading: boolean;

  /**
   * Error state from the query
   */
  error?: Error;

  /**
   * Refetch function to refresh the data
   */
  refetch?: () => void;
};

export type CourseProgressModuleProgressContextType = ContextData;

const CourseProgressModuleProgressContext = React.createContext<ContextData | null>(null);

/**
 * Provider for course module progress data.
 * Fetches module completion status for a given course.
 */
const CourseProgressModuleProgressProvider = ({ children, courseId, skip = false }: Props) => {
  const { data, loading, error, refetch } = useQuery<
    GetCourseBranchModuleProgressByCourseQuery,
    GetCourseBranchModuleProgressByCourseQueryVariables
  >(queryCourseBranchModuleProgressByCourse, {
    variables: { courseId },
    fetchPolicy: 'cache-and-network',
    context: { clientName: 'gatewayGql' },
    skip: !courseId || skip,
  });

  const moduleProgressData = data?.CourseProgress?.queryCourseBranchModuleProgressByCourse?.map((progress) => ({
    moduleId: progress.moduleId,
    moduleIndex: progress.moduleIndex,
    moduleCompletionStatus: progress.moduleCompletionStatus,
  }));

  const contextValue: ContextData = {
    id: courseId ? `${user.get().id}-${courseId}` : undefined,
    moduleProgressData,
    loading,
    error: error ? new Error(error.message) : undefined,
    refetch,
  };

  return (
    <CourseProgressModuleProgressContext.Provider value={contextValue}>
      {children}
    </CourseProgressModuleProgressContext.Provider>
  );
};

export const useCourseProgressModuleProgressContext = () => {
  const context = React.useContext(CourseProgressModuleProgressContext);
  if (!context) {
    return {
      id: undefined,
      moduleProgressData: undefined,
      loading: false,
      error: undefined,
      refetch: undefined,
    };
  }
  return context;
};

export default CourseProgressModuleProgressProvider;
